import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Download, FileSignature, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError, API_BASE } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import SignaturePad from '../components/SignaturePad';

const STATUS_BADGE = {
    issued:              { label: 'Awaiting your signature', color: 'bg-sky-100 text-sky-800' },
    viewed:              { label: 'Awaiting your signature', color: 'bg-amber-100 text-amber-800' },
    signed_by_recipient: { label: 'Awaiting counter-signature from Beyond Measure', color: 'bg-violet-100 text-violet-800' },
    signed:              { label: 'Fully signed', color: 'bg-emerald-100 text-emerald-800' },
    expired:             { label: 'Expired', color: 'bg-rose-100 text-rose-800' },
    cancelled:           { label: 'Cancelled', color: 'bg-slate-200 text-slate-600' },
};

export default function PublicNDA() {
    const { token } = useParams();
    const [nda, setNda]  = useState(null);
    const [err, setErr]  = useState(null);
    const [signing, setSigning] = useState(false);
    const [form, setForm] = useState({
        typed_signature: '',
        signature_image_base64: null,
        title: '',
        confirmed_company: '',
        confirmed_address: '',
        accept_terms: false,
    });
    const startedAt = useRef(Date.now());

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/ndas/public/${token}`);
            setNda(r.data);
            // Pre-fill confirmed_* from existing values so recipient can edit if needed
            setForm(f => ({
                ...f,
                confirmed_company: r.data.recipient_company || '',
                confirmed_address: r.data.recipient_address || '',
            }));
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || 'NDA not available');
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    // Heartbeat: send dwell every 20s (light audit per user spec)
    useEffect(() => {
        if (!nda?.session_id) return undefined;
        const tick = async () => {
            try {
                await api.post(`/ndas/public/${token}/heartbeat`, {
                    session_id: nda.session_id,
                    dwell_seconds: Math.round((Date.now() - startedAt.current) / 1000),
                });
            } catch { /* ignore */ }
        };
        const handle = setInterval(tick, 20000);
        const onHide = () => tick();
        window.addEventListener('pagehide', onHide);
        return () => { clearInterval(handle); window.removeEventListener('pagehide', onHide); };
    }, [nda?.session_id, token]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const sign = async () => {
        if (!form.accept_terms) return toast.error('Please tick the acceptance box.');
        if (!form.typed_signature.trim()) return toast.error('Please type your full name.');
        setSigning(true);
        try {
            await api.post(`/ndas/public/${token}/sign`, form);
            toast.success('Thank you — your signature has been recorded.');
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Signing failed');
        } finally {
            setSigning(false);
        }
    };

    if (err) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="bg-white rounded-lg p-8 max-w-md text-center border border-slate-200 shadow-sm">
                    <div className="text-rose-600 font-semibold">Unable to load NDA</div>
                    <div className="text-sm text-slate-500 mt-2">{err}</div>
                </div>
            </div>
        );
    }
    if (!nda) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

    const badge = STATUS_BADGE[nda.status] || STATUS_BADGE.viewed;
    const fullySigned = nda.status === 'signed';
    const canSign = nda.can_sign;

    return (
        <div className="min-h-screen bg-slate-50" data-testid="public-nda">
            <header className="bg-[color:var(--bm-primary,#0B3C5D)] text-white px-6 py-4">
                <div className="max-w-3xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-[color:var(--bm-accent,#3BC6B4)]" />
                        <div className="text-sm font-semibold">{nda.bm_company?.name || 'Beyond Measure'}</div>
                    </div>
                    <div className={`text-[11px] font-semibold px-2 py-1 rounded-full ${badge.color}`}>
                        {badge.label}
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
                <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                        {nda.nda_number}
                    </div>
                    <h1 className="text-[22px] font-bold text-slate-900 mt-1">{nda.title}</h1>
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[13.5px] text-slate-800 mt-4 leading-relaxed">
                        <RenderedBody body={nda.rendered_preview} />
                    </div>
                </div>

                {fullySigned ? (
                    <div className="bg-white rounded-lg border border-emerald-200 p-6 shadow-sm" data-testid="public-nda-signed">
                        <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                            <CheckCircle2 className="w-5 h-5" />
                            Fully signed by both parties
                        </div>
                        <p className="text-[13px] text-slate-600 mt-1.5">
                            Signed by {nda.recipient_signature?.typed_name} on {fmtTs(nda.recipient_signed_at)} ·
                            Counter-signed by {nda.counter_signature?.typed_name} on {fmtTs(nda.counter_signed_at)}.
                        </p>
                        <Button
                            className="mt-3"
                            onClick={() => window.open(`${API_BASE}/ndas/public/${token}/pdf`, '_blank')}
                            data-testid="public-pdf-btn"
                        >
                            <Download className="w-4 h-4 mr-1" /> Download signed PDF
                        </Button>
                    </div>
                ) : nda.status === 'signed_by_recipient' ? (
                    <div className="bg-white rounded-lg border border-violet-200 p-6 shadow-sm">
                        <div className="flex items-center gap-2 text-violet-700 font-semibold">
                            <FileSignature className="w-5 h-5" />
                            Awaiting counter-signature
                        </div>
                        <p className="text-[13px] text-slate-600 mt-1.5">
                            Thank you {nda.recipient_signature?.typed_name}. We'll counter-sign shortly — you'll receive a copy of the fully signed PDF once that's done.
                        </p>
                    </div>
                ) : canSign ? (
                    <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm" data-testid="public-nda-sign-form">
                        <h2 className="text-[16px] font-semibold text-slate-900">Confirm your details and sign</h2>
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <PFieldRow label="Your full name *" wide>
                                <Input value={nda.recipient_name} disabled className="bg-slate-50" />
                            </PFieldRow>
                            <PFieldRow label="Email">
                                <Input value={nda.recipient_email} disabled className="bg-slate-50" />
                            </PFieldRow>
                            <PFieldRow label="Your title">
                                <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Consultant" data-testid="public-title" />
                            </PFieldRow>
                            <PFieldRow label="Company / trading name" wide>
                                <Input value={form.confirmed_company} onChange={(e) => set('confirmed_company', e.target.value)} placeholder="(your company name)" data-testid="public-company" />
                            </PFieldRow>
                            <PFieldRow label="Address" wide>
                                <Input value={form.confirmed_address} onChange={(e) => set('confirmed_address', e.target.value)} placeholder="Registered or trading address" data-testid="public-address" />
                            </PFieldRow>
                        </div>

                        <div className="mt-5">
                            <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Signature</Label>
                            <div className="mt-1.5">
                                <SignaturePad
                                    onDrawnChange={(d) => set('signature_image_base64', d)}
                                    onTypedChange={(v) => set('typed_signature', v)}
                                    typedValue={form.typed_signature}
                                />
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1.5">Typed signature is stored on the record either way. If you also draw, the drawing is embedded in the PDF.</div>
                            <div className="mt-2">
                                <Label className="text-[10.5px] uppercase tracking-wide text-slate-500 font-semibold">Typed signature for the record *</Label>
                                <Input value={form.typed_signature} onChange={(e) => set('typed_signature', e.target.value)} placeholder="Type your full name" className="mt-1" data-testid="public-typed-sig" />
                            </div>
                        </div>

                        <label className="flex items-start gap-2 mt-5 cursor-pointer" htmlFor="accept-terms-cb">
                            <Checkbox
                                id="accept-terms-cb"
                                checked={form.accept_terms}
                                onCheckedChange={(c) => set('accept_terms', !!c)}
                                data-testid="public-accept-terms"
                            />
                            <span className="text-[13px] text-slate-700">
                                I confirm I have authority to bind {form.confirmed_company || '[Recipient party]'} and I agree to the Non-Disclosure Agreement above.
                            </span>
                        </label>

                        <div className="mt-5">
                            <Button onClick={sign} disabled={signing || !form.accept_terms || !form.typed_signature.trim()} data-testid="public-sign-submit">
                                <FileSignature className="w-4 h-4 mr-1" /> {signing ? 'Signing…' : 'Sign NDA'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                        <div className="text-[14px] font-semibold text-slate-700">This NDA is not currently available for signing.</div>
                        <div className="text-[12px] text-slate-500 mt-1">Status: {nda.status}. Please contact Beyond Measure if you believe this is in error.</div>
                    </div>
                )}

                <footer className="text-center text-[11px] text-slate-400 py-4">
                    Powered by Beyond Measure HQ · {nda.bm_company?.name}
                </footer>
            </main>
        </div>
    );
}


function PFieldRow({ label, children, wide }) {
    return (
        <div className={wide ? 'col-span-2' : ''}>
            <Label className="text-[10.5px] uppercase tracking-wide text-slate-500 font-medium">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}


function RenderedBody({ body }) {
    if (!body) return null;
    const lines = body.split('\n');
    return (
        <>
            {lines.map((line, i) => {
                if (line.startsWith('# ')) return <h2 key={i} className="text-[18px] font-bold text-[color:var(--bm-primary,#0B3C5D)] mt-4 mb-2">{line.slice(2)}</h2>;
                if (line.startsWith('## ')) return <h3 key={i} className="text-[15px] font-semibold mt-4 mb-1.5 text-slate-800">{line.slice(3)}</h3>;
                if (line.startsWith('* ')) return <li key={i} className="ml-5 list-disc">{inline(line.slice(2))}</li>;
                if (!line.trim()) return <div key={i} className="h-2" />;
                return <p key={i} className="mb-1.5">{inline(line)}</p>;
            })}
        </>
    );
}

function inline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*)/);
    return parts.map((p, i) => (
        p.startsWith('**') && p.endsWith('**')
            ? <strong key={i}>{p.slice(2, -2)}</strong>
            : <React.Fragment key={i}>{p}</React.Fragment>
    ));
}

function fmtTs(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
}
